import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { AIEntryProvider } from "@/contexts/AIEntryContext";
import { GlobalLoadingProvider } from "@/contexts/GlobalLoadingContext";
import { UnsavedChangesProvider } from "@/contexts/UnsavedChangesContext";
import { UsersCacheProvider } from "@/contexts/UsersCacheContext";
import NavigationGuard from "@/components/NavigationGuard";
import NetworkStatus from "@/components/NetworkStatus";
import { usePermissions, getScreenKeyFromPath } from "@/hooks/usePermissions";
import AppLayout from "@/components/AppLayout";
import { useEffect, lazy, Suspense } from "react";
import { getSavedTheme, applyBrightness, getSavedBrightness, getSavedDarkMode } from "@/hooks/useColorTheme";
import { hasTenantCustomTheme } from "@/contexts/TenantContext";

import Auth from "./pages/Auth";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import NotFound from "./pages/NotFound";
import { useTenant } from "@/contexts/TenantContext";

// All heavy pages are lazy-loaded so /auth and initial paint stay snappy.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Shipments = lazy(() => import("./pages/Shipments"));
const ShipmentDetail = lazy(() => import("./pages/ShipmentDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Entry = lazy(() => import("./pages/Entry"));
const FollowUp = lazy(() => import("./pages/FollowUp"));
const Accounting = lazy(() => import("./pages/Accounting"));
const DriverApp = lazy(() => import("./pages/DriverApp"));
const CustomerApp = lazy(() => import("./pages/CustomerApp"));
const Customers = lazy(() => import("./pages/Customers"));
const Drivers = lazy(() => import("./pages/Drivers"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Install = lazy(() => import("./pages/Install"));
const ChangeCredentials = lazy(() => import("./pages/ChangeCredentials"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const DISABLE_AUTH = false; // Auth gate active

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, role } = useAuth();

  if (DISABLE_AUTH) return <>{children}</>;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const RoleRedirect = ({ children }: { children: React.ReactNode }) => {
  const { role, mustChangeCredentials } = useAuth();
  const location = useLocation();

  // Super admin goes to super-admin dashboard
  if (role === 'super_admin' && location.pathname !== '/super-admin' && location.pathname !== '/change-credentials') {
    return <Navigate to="/super-admin" replace />;
  }

  // Redirect drivers immediately before any child component renders
  if (role === 'driver' && location.pathname !== '/driver-app' && location.pathname !== '/change-credentials') {
    return <Navigate to="/driver-app" replace />;
  }

  // Redirect customers immediately before any child component renders
  if (role === 'customer' && location.pathname !== '/customer-app' && location.pathname !== '/change-credentials') {
    return <Navigate to="/customer-app" replace />;
  }

  // Force credential change before anything else renders
  if (mustChangeCredentials && location.pathname !== '/change-credentials') {
    return <Navigate to="/change-credentials" replace />;
  }

  return <>{children}</>;
};

const PermissionGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { canAccessScreen } = usePermissions();
  const screenKey = getScreenKeyFromPath(location.pathname);

  if (screenKey && !canAccessScreen(screenKey)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const ColorThemeInitializer = () => {
  useEffect(() => {
    // Skip if tenant has custom branding — don't overwrite company colors
    if (hasTenantCustomTheme()) {
      // Still apply dark mode preference
      const darkMode = getSavedDarkMode();
      document.documentElement.classList.toggle('dark', darkMode);
      return;
    }
    const darkMode = getSavedDarkMode();
    document.documentElement.classList.toggle('dark', darkMode);
    applyBrightness(getSavedBrightness(), getSavedTheme());
  }, []);
  return null;
};

const LazyFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const AuthOrSuperAdmin = () => {
  const { isSuperAdminDomain } = useTenant();
  if (isSuperAdminDomain) return <Navigate to="/super-admin-login" replace />;
  return <Auth />;
};

const SuperAdminDomainRedirect = () => {
  const { isSuperAdminDomain } = useTenant();
  if (isSuperAdminDomain) return <Navigate to="/super-admin-login" replace />;
  return null;
};

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TenantProvider>
      <AuthProvider>
        <UsersCacheProvider>
        <GlobalLoadingProvider>
        <AIEntryProvider>
        <TooltipProvider>
          <ColorThemeInitializer />
          <NetworkStatus />
          <Toaster />
          <Sonner />
           <BrowserRouter>
            <UnsavedChangesProvider>
            <NavigationGuard />
            <Routes>
              <Route path="/auth" element={<AuthOrSuperAdmin />} />
              <Route path="/super-admin-login" element={<SuperAdminLogin />} />
              <Route path="/install" element={<Suspense fallback={<LazyFallback />}><Install /></Suspense>} />
              <Route path="/change-credentials" element={<ProtectedRoute><Suspense fallback={<LazyFallback />}><ChangeCredentials /></Suspense></ProtectedRoute>} />
              <Route path="/super-admin" element={<ProtectedRoute><Suspense fallback={<LazyFallback />}><SuperAdmin /></Suspense></ProtectedRoute>} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <RoleRedirect>
                    <AppLayout>
                      <PermissionGuard>
                        <Suspense fallback={<LazyFallback />}>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/entry" element={<Entry />} />
                          <Route path="/shipments" element={<Shipments />} />
                          <Route path="/shipments/:id" element={<ShipmentDetail />} />
                          <Route path="/follow-up" element={<FollowUp />} />
                          <Route path="/accounting" element={<Accounting />} />
                          <Route path="/driver-app" element={<DriverApp />} />
                          <Route path="/customer-app" element={<CustomerApp />} />
                          <Route path="/customers" element={<Customers />} />
                          <Route path="/drivers" element={<Drivers />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                        </Suspense>
                      </PermissionGuard>
                    </AppLayout>
                    </RoleRedirect>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </UnsavedChangesProvider>
          </BrowserRouter>
        </TooltipProvider>
        </AIEntryProvider>
        </GlobalLoadingProvider>
        </UsersCacheProvider>
      </AuthProvider>
      </TenantProvider>
    </LanguageProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
